import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { type ReactElement } from 'react';
import { fromEntries as rFromEntries, map as rMap, pick as rPick, pipe as rPipe } from 'remeda';
import {
  array as vArray,
  boolean as vBoolean,
  fallback as vFallback,
  nullish as vNullish,
  number as vNumber,
  object as vObject,
  string as vString,
  unknown as vUnknown,
} from 'valibot';

import { SecondaryActionsIconButton } from '@/components/secondary-actions-icon-button.component';
import { LOCAL_STORAGE } from '@/constants/storage';
import { CampaignRequestStatusEnum, type operations } from '@/libs/openapi';
import {
  Breadcrumb,
  Breadcrumbs,
  HelpCircleStrokeIcon,
  Text,
  type SkeletonRow,
  useLocalStorage,
  useMediaQuery,
} from '@/libs/serenity-ui';
import { breakpoints } from '@/libs/serenity-ui/constants.stylex';
import { RouterLink, RouterMenuItemLink } from '@/libs/tanstack-router';
import { campaignRequestQueries, type CampaignRequestListQueryResultData } from '@/resources/campaign-request/campaign-request.api';
import { toApiCampaignRequestFieldAdapter } from '@/resources/campaign-request/campaign-request.field-adapter';
import { campaignFilterListAdapter } from '@/resources/campaign-request/campaign-request.filter-list-adapter';
import { campaignRequestPolicies } from '@/resources/campaign-request/campaign-request.policies';
import { campaignRequestTableInitialConfig, campaignRequestTableOptions } from '@/resources/campaign-request/campaign-request.table';
import { campaignRequestProgramQueries } from '@/resources/campaign-request-program/campaign-request-program.api';
import { organizationTeamQueries } from '@/resources/organization-team/organization-team.api';
import { organizationUserQueries } from '@/resources/organization-user/organization-user.api';
import { ActiveFilters } from '@/routes/_main/_list/-components/active-filters.component';
import { FullscreenTable } from '@/routes/_main/_list/-components/fullscreen-table.component';
import { Pagination } from '@/routes/_main/_list/-components/pagination.component';
import { Search } from '@/routes/_main/_list/-components/search.component';
import { CampaignRequestsFiltersForm } from '@/routes/_main/_list/campaigns/-components/campaign-requests-filters-form.component';
import { DuplicateCampaignRequestMenuItem } from '@/routes/_main/_list/campaigns/-components/duplicate-campaign-request-menu-item.component';
import { UnarchiveCampaignRequestMenuItem } from '@/routes/_main/_list/campaigns/-components/unarchive-campaign-request-menu-item.component';
import { Resource } from '@/types/resource.type';

const DEFAULT_SORTING = { id: 'createdAt', desc: true };
const DEFAULT_PAGING = { pageIndex: 0, pageSize: 100 };
const SEARCH_FILTER_ID: keyof Exclude<operations['CampaignRequestController_getMany']['parameters']['query'], undefined> = 'campaignName';

export const Route = createFileRoute('/_main/_list/campaigns/archive')({
  component: RouteComponent,
  validateSearch: vObject({
    pagination: vFallback(vNullish(vObject({ pageIndex: vNumber(), pageSize: vNumber() }), DEFAULT_PAGING), DEFAULT_PAGING),
    columnFilters: vFallback(vNullish(vArray(vObject({ id: vString(), value: vUnknown() })), []), []),
    sorting: vFallback(vNullish(vObject({ id: vString(), desc: vBoolean() }), DEFAULT_SORTING), DEFAULT_SORTING),
  }),
  beforeLoad: ({ search, context }) => {
    if (context.user && !context.can.campaignRequests.view()) {
      throw redirect({ to: '/' });
    }

    const ownerFilter = search.columnFilters.find((f) => f.id === 'ownerId');
    const teamFilter = search.columnFilters.find((f) => f.id === 'teamId');
    const programFilter = search.columnFilters.find((f) => f.id === 'programId');

    return {
      resource: `${Resource.campaignRequest}Archive` as const,
      can: {
        [Resource.campaignRequest as const]: rPick(campaignRequestPolicies.bindContext({ user: context.user.original, organization: context.organization?.original }), [ 'create', 'unarchive' ]),
      },
      queryOptions: {
        list: campaignRequestQueries.list({
          paging: search.pagination,
          sorting: search.sorting,
          filter: {
            ...toApiCampaignRequestFieldAdapter.transform(rPipe(
              search.columnFilters,
              rMap(({ id, value }) => [ id, value ] as const),
              rFromEntries(),
            )),
            archived: true,
          },
        }),
        organizationUserListFilter: organizationUserQueries.list(ownerFilter ? { id: ownerFilter.value as string } : undefined),
        organizationTeamListFilter: organizationTeamQueries.list(teamFilter ? { id: teamFilter.value as number } : undefined),
        programListFilter: campaignRequestProgramQueries.list(
          programFilter
            ? { paging: { pageIndex: 0, pageSize: 1 }, filter: { id: [ programFilter.value as number ] }}
            : undefined,
        ),
      },
      hasOwnerFilter: !!ownerFilter,
      hasTeamFilter: !!teamFilter,
      hasProgramFilter: !!programFilter,
    };
  },
  loader: ({ context: { queryClient, queryOptions, hasOwnerFilter, hasTeamFilter, hasProgramFilter }}) => {
    queryClient.prefetchQuery(queryOptions.list);
    hasOwnerFilter && queryClient.prefetchQuery(queryOptions.organizationUserListFilter);
    hasTeamFilter && queryClient.prefetchQuery(queryOptions.organizationTeamListFilter);
    hasProgramFilter && queryClient.prefetchQuery(queryOptions.programListFilter);
  },
  staticData: {
    list: {
      heading: 'Archive',
      slots: {
        topBarComponent: TopBarComponent,
        toolbarLeftComponent: ToolbarLeftComponent,
        toolbarActionsComponent: ToolbarActionsComponent,
        bottomBarComponent: Pagination,
      },
    },
  },
  head: () => ({
    meta: [
      {
        title: 'TrendMD | Archived Campaigns',
      },
    ],
  }),
});

function RouteComponent(): ReactElement {
  const routeContext = Route.useRouteContext();

  const [ columnsConfig ] = useLocalStorage(LOCAL_STORAGE.columnsConfig(routeContext.resource as Resource).key, campaignRequestTableInitialConfig);

  const query = useQuery(routeContext.queryOptions.list);

  const config = campaignRequestTableOptions(query.data?.data ?? [], columnsConfig);
  const rows: (CampaignRequestListQueryResultData | SkeletonRow)[] = query.isLoading ? config.getSkeletonRows() : query.data?.data ?? [];

  return (
    <FullscreenTable
      ariaLabel="Archived campaigns"
      config={config}
      getRowId={(row) => row.original.id.toString()}
      isLoading={query.isLoading}
      rows={rows}
      getRowVariant={(row) => (
        row.original.status === CampaignRequestStatusEnum.DECLINED ||
        row.original.status === CampaignRequestStatusEnum.ENDED ? 'inactive' : undefined
      )}
      renderExtraMenuItems={(row) => (
        <>
          {routeContext.can.campaignRequest.create() && <DuplicateCampaignRequestMenuItem {...row} />}
          {/* archivedAt is not included in the list DTO — all items on the archive page are archived by definition, so we pass a non-null sentinel to satisfy the policy check */}
          {routeContext.can.campaignRequest.unarchive({ ...row.original, archivedAt: '' }) && <UnarchiveCampaignRequestMenuItem {...row} />}
        </>
      )}
    />
  );
}

function TopBarComponent(): ReactElement {
  return (
    <Breadcrumbs>
      <Breadcrumb>
        <RouterLink to="/campaigns">Campaigns</RouterLink>
      </Breadcrumb>
      <Breadcrumb hidden>
        <RouterLink to="/campaigns/archive">Archive</RouterLink>
      </Breadcrumb>
    </Breadcrumbs>
  );
}

function ToolbarActionsComponent(): ReactElement | false {
  const isSmallScreen = useMediaQuery(breakpoints.small);
  return !isSmallScreen && (
    <SecondaryActionsIconButton isTableActions>
      <RouterMenuItemLink to="/campaigns/archive/guide">
        <HelpCircleStrokeIcon />
        <Text>Help</Text>
      </RouterMenuItemLink>
    </SecondaryActionsIconButton>
  );
}

function ToolbarLeftComponent(): ReactElement {
  const routeContext = Route.useRouteContext();
  const isSmallScreen = useMediaQuery(breakpoints.small);

  const organizationUserListFilterQuery = useQuery({
    ...routeContext.queryOptions.organizationUserListFilter,
    enabled: routeContext.hasOwnerFilter,
  });
  const organizationTeamListFilterQuery = useQuery({
    ...routeContext.queryOptions.organizationTeamListFilter,
    enabled: routeContext.hasTeamFilter,
  });
  const programListFilterQuery = useQuery({
    ...routeContext.queryOptions.programListFilter,
    enabled: routeContext.hasProgramFilter,
  });

  return (
    <>
      <Search resource={routeContext.resource as Resource} searchFilterId={SEARCH_FILTER_ID}>
        {(renderProps) => <CampaignRequestsFiltersForm from="/_main/_list/campaigns/archive" onClose={renderProps.close} />}
      </Search>
      <ActiveFilters
        searchFilterId={SEARCH_FILTER_ID}
        filterListAdapter={(filters) => campaignFilterListAdapter(filters, {
          owner: organizationUserListFilterQuery.data?.data?.[0]?.original,
          team: organizationTeamListFilterQuery.data?.data?.[0]?.original,
          program: programListFilterQuery.data?.data?.[0]?.original,
          isOwnerPending: organizationUserListFilterQuery.isPending,
          isTeamPending: organizationTeamListFilterQuery.isPending,
          isProgramPending: programListFilterQuery.isPending,
        })}
      />
      {isSmallScreen &&
        <SecondaryActionsIconButton isTableActions>
          <RouterMenuItemLink to="/campaigns/archive/guide">
            <HelpCircleStrokeIcon />
            <Text>Help</Text>
          </RouterMenuItemLink>
        </SecondaryActionsIconButton>}
    </>
  );
}
